import { memo, useMemo } from 'react'
import { List, Map } from 'immutable'
import InlineSelect, { InlineSelectLabel } from '@aytm/lib/rfd-ui/components/basic/inline-select'
import { decodeText } from '@aytm/app/stats/lib/util/helpers'

import useImages from '../states/useImages'
import useModes from '../states/useModes'
import useSelectedItems from '../states/useSelectedItems'
import { attributeHeadPositions } from '../states/useTable'

import { CompareItemsProps } from '../types'
import { DEFAULT_MAIN_ANALYSIS_NAME, DEFAULT_SIGTEST_ANALYSIS_NAME } from '../config/CompareItems_const'

import { Checkbox } from 'primereact/checkbox'

import style from './CompareItemsModesBar.module.scss'

export type CompareItemsModesBarProps = {
  enableSigtestAnalysis: CompareItemsProps['enableSigtestAnalysis']
  mainAnalysisName: CompareItemsProps['mainAnalysisName']
  sigtestAnalysisName: CompareItemsProps['sigtestAnalysisName']
  presets: CompareItemsProps['presets']
  attributes: CompareItemsProps['attributes']
  canUseImages: CompareItemsProps['canUseImages']
}

export default memo(
  CompareItemsModesBar,
  (prevProps, nextProps) =>
    prevProps.enableSigtestAnalysis === nextProps.enableSigtestAnalysis &&
    prevProps.mainAnalysisName === nextProps.mainAnalysisName &&
    prevProps.sigtestAnalysisName === nextProps.sigtestAnalysisName &&
    prevProps.presets === nextProps.presets &&
    prevProps.attributes === nextProps.attributes &&
    prevProps.canUseImages === nextProps.canUseImages
)

export function CompareItemsModesBar(props: CompareItemsModesBarProps) {
  const {
    enableSigtestAnalysis,
    mainAnalysisName,
    sigtestAnalysisName,
    presets,
    attributes,
    canUseImages = true
  } = props

  const { analysisMode, setAnalysisMode, fullTexts, setFullTexts } = useModes()
  const { showImages, setShowImages } = useImages()
  const { presetIndex, setPresetIndex, sortByAttributeIndex, setSortByAttribute } = useSelectedItems()

  const fetchAnalysisOptions = useMemo(
    () => () =>
      List([
        Map({
          value: 'main',
          render: mainAnalysisName || DEFAULT_MAIN_ANALYSIS_NAME
        }),
        Map({
          value: 'sigtest',
          render: sigtestAnalysisName || DEFAULT_SIGTEST_ANALYSIS_NAME
        })
      ]),
    [mainAnalysisName, sigtestAnalysisName]
  )
  const analysisText = useMemo(() => {
    switch (analysisMode) {
      case 'sigtest': {
        return sigtestAnalysisName || DEFAULT_SIGTEST_ANALYSIS_NAME
      }
      case 'main': {
        return mainAnalysisName || DEFAULT_MAIN_ANALYSIS_NAME
      }
      default: {
        return ''
      }
    }
  }, [analysisMode, mainAnalysisName, sigtestAnalysisName])

  const fetchPresetOptions = useMemo(
    () => () =>
      List(
        presets
          .map((preset, index) =>
            false === preset?.hidden && preset?.text
              ? Map({
                  value: index,
                  render: () => <div className={style.CompareItemsModesBar__preset__option}>{preset.text}</div>
                })
              : null
          )
          .filter(Boolean)
      ),
    [presets]
  )
  const presetText = useMemo(
    () => (null == presetIndex || null == presets[presetIndex] ? 'custom' : presets[presetIndex].text),
    [presetIndex, presets]
  )

  // labels are optional for a run, so an unlabelled attribute is named by the `N.` its row head draws
  const rankByLabels = useMemo(() => {
    const headPositions = attributeHeadPositions(attributes)
    return attributes.map((attribute, index) =>
      attribute?.text ? decodeText(attribute.text) : null != headPositions[index] ? `${headPositions[index]}.` : null
    )
  }, [attributes])

  const fetchSortByAttributeOptions = useMemo(
    () => () =>
      List(
        rankByLabels
          .map((label, index) =>
            null != label
              ? Map({
                  value: index,
                  render: () => <div className={style.CompareItemsModesBar__attributeSort__option}>{label}</div>
                })
              : null
          )
          .filter(Boolean)
      ),
    [rankByLabels]
  )
  const sortByAttributeText = useMemo(() => {
    if (null == sortByAttributeIndex || null == attributes[sortByAttributeIndex]) {
      return 'custom'
    }
    return rankByLabels[sortByAttributeIndex] ?? ''
  }, [sortByAttributeIndex, attributes, rankByLabels])

  return (
    <div className={style.CompareItemsModesBar}>
      {enableSigtestAnalysis ? (
        <InlineSelectLabel className={style.CompareItemsModesBar__analysisMode}>
          Analysis:&nbsp;
          <InlineSelect
            fetchOptions={fetchAnalysisOptions}
            onSelect={(option: Map<string, string>) => {
              setAnalysisMode(option.get('value') as CompareItemsProps['analysisMode'])
            }}
            value={analysisMode}
          >
            <b>{analysisText}</b>
          </InlineSelect>
        </InlineSelectLabel>
      ) : null}
      <div className={style.CompareItemsModesBar__spacer} />
      {canUseImages ? (
        <label className={style.CompareItemsModesBar__showImages}>
          <Checkbox
            className={`${style.CompareItemsModesBar__showImages__checkbox} p-checkbox-sm`}
            checked={showImages}
            onChange={() => void setShowImages(!showImages)}
          />
          Show images
        </label>
      ) : null}
      <label className={style.CompareItemsModesBar__fullTexts}>
        <Checkbox
          className={`${style.CompareItemsModesBar__fullTexts__checkbox} p-checkbox-sm`}
          checked={fullTexts}
          onChange={() => void setFullTexts(!fullTexts)}
        />
        Full text
      </label>
      <InlineSelectLabel className={style.CompareItemsModesBar__preset}>
        Show:&nbsp;
        <InlineSelect
          fetchOptions={fetchPresetOptions}
          onSelect={(option: Map<string, number>) => {
            setPresetIndex(option.get('value'))
          }}
          value={presetIndex}
        >
          <b>{presetText}</b>
        </InlineSelect>
      </InlineSelectLabel>
      <InlineSelectLabel className={style.CompareItemsModesBar__attributeSort}>
        Rank by:&nbsp;
        <InlineSelect
          fetchOptions={fetchSortByAttributeOptions}
          onSelect={(option: Map<string, number>) => {
            setSortByAttribute(option.get('value'))
          }}
          value={sortByAttributeIndex}
        >
          <b>{sortByAttributeText}</b>
        </InlineSelect>
      </InlineSelectLabel>
    </div>
  )
}
